import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/contexts/ToastContext';
import { supabase } from '@/lib/supabase';
import type { Category, Subcategory } from '@/types/common';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import IconSelect from '@/components/Form/IconSelect';
import CategoryModal from '@/components/Settings/CategoryModal';
import SubcategoryModal from '@/components/Settings/SubcategoryModal';
import ProviderModal from '@/components/Settings/ProviderModal';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Tab } from '@headlessui/react';
import { classNames } from '@/utils/classNames';
import Modal from '@/components/common/Modal';

interface Provider {
  id: string;
  name: string;
  icon: string;
  created_at: string;
}

export default function SettingsPage() {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<Subcategory | null>(null);
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [isDeleteCategoryModalOpen, setIsDeleteCategoryModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [isDeleteSubcategoryModalOpen, setIsDeleteSubcategoryModalOpen] = useState(false);
  const [subcategoryToDelete, setSubcategoryToDelete] = useState<string | null>(null);
  const [isDeleteProviderModalOpen, setIsDeleteProviderModalOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<string | null>(null);

  // Available FontAwesome icons for categories
  const availableIcons = [
    { name: 'Utensils', icon: 'fa-utensils' },
    { name: 'Wine Glass', icon: 'fa-wine-glass' },
    { name: 'Beer', icon: 'fa-beer' },
    { name: 'Cheese', icon: 'fa-cheese' },
    { name: 'Ice Cream', icon: 'fa-ice-cream' },
    { name: 'Pizza Slice', icon: 'fa-pizza-slice' },
    { name: 'Coffee', icon: 'fa-coffee' },
    { name: 'Glass Martini', icon: 'fa-glass-martini' },
    { name: 'Hamburger', icon: 'fa-hamburger' },
    { name: 'Fish', icon: 'fa-fish' },
    { name: 'Bread Slice', icon: 'fa-bread-slice' },
    { name: 'Egg', icon: 'fa-egg' },
    { name: 'Pepper Hot', icon: 'fa-pepper-hot' },
    { name: 'Apple Alt', icon: 'fa-apple-alt' },
    { name: 'Carrot', icon: 'fa-carrot' },
    { name: 'Lemon', icon: 'fa-lemon' },
    { name: 'Cookie', icon: 'fa-cookie' },
    { name: 'Candy Cane', icon: 'fa-candy-cane' },
    { name: 'Cocktail', icon: 'fa-cocktail' },
    { name: 'Glass Whiskey', icon: 'fa-glass-whiskey' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch categories with cache disabled
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('categories')
          .select('*')
          .order('order_index')
          .abortSignal(new AbortController().signal); // This disables caching

        if (categoriesError) throw categoriesError;

        // Fetch subcategories with cache disabled
        const { data: subcategoriesData, error: subcategoriesError } = await supabase
          .from('subcategories')
          .select('*')
          .order('category_id')
          .order('order_index')
          .abortSignal(new AbortController().signal); // This disables caching

        if (subcategoriesError) throw subcategoriesError;

        // Fetch providers with cache disabled
        const { data: providersData, error: providersError } = await supabase
          .from('providers')
          .select('*')
          .order('created_at', { ascending: false })
          .abortSignal(new AbortController().signal);

        if (providersError) throw providersError;

        setCategories(categoriesData || []);
        setSubcategories(subcategoriesData || []);
        setProviders(providersData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        addToast({
          type: 'error',
          message: 'Failed to load data',
        });
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchData();

    // Subscribe to changes
    const categoriesChannel = supabase
      .channel('categories-changes')
      .on('postgres_changes', {
        event: '*', // Listen to all events
        schema: 'public',
        table: 'categories'
      }, async () => {
        // Refetch data when any change occurs
        await fetchData();
      })
      .subscribe();

    const subcategoriesChannel = supabase
      .channel('subcategories-changes')
      .on('postgres_changes', {
        event: '*', // Listen to all events
        schema: 'public',
        table: 'subcategories'
      }, async () => {
        // Refetch data when any change occurs
        await fetchData();
      })
      .subscribe();

    const providersChannel = supabase
      .channel('providers-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'providers'
      }, async () => {
        await fetchData();
      })
      .subscribe();

    return () => {
      categoriesChannel.unsubscribe();
      subcategoriesChannel.unsubscribe();
      providersChannel.unsubscribe();
    };
  }, [addToast]);

  const handleCreateCategory = async (data: { name: string; icon: string }) => {
    try {
      const { error } = await supabase
        .from('categories')
        .insert([
          {
            name: data.name,
            icon: data.icon,
            order_index: categories.length
          }
        ])
        .abortSignal(new AbortController().signal); // Disable cache for insert

      if (error) throw error;

      addToast({
        type: 'success',
        message: 'Category created successfully',
      });
      setShowCategoryModal(false);
    } catch (error) {
      console.error('Error creating category:', error);
      addToast({
        type: 'error',
        message: 'Failed to create category',
      });
    }
  };

  const handleUpdateCategory = async (data: { name: string; icon: string }) => {
    if (!editingCategory) return;

    try {
      const { error } = await supabase
        .from('categories')
        .update({
          name: data.name,
          icon: data.icon
        })
        .eq('id', editingCategory.id)
        .abortSignal(new AbortController().signal); // Disable cache for update

      if (error) throw error;

      addToast({
        type: 'success',
        message: 'Category updated successfully',
      });
      setEditingCategory(null);
      setShowCategoryModal(false);
    } catch (error) {
      console.error('Error updating category:', error);
      addToast({
        type: 'error',
        message: 'Failed to update category',
      });
    }
  };

  const handleCreateSubcategory = async (data: { name: string; categoryId: string }) => {
    try {
      const existingSubcategories = subcategories.filter(
        s => s.category_id === data.categoryId
      );

      const { error } = await supabase
        .from('subcategories')
        .insert([
          {
            name: data.name,
            category_id: data.categoryId,
            order_index: existingSubcategories.length
          }
        ])
        .abortSignal(new AbortController().signal); // Disable cache for insert

      if (error) throw error;

      addToast({
        type: 'success',
        message: 'Subcategory created successfully',
      });
      setShowSubcategoryModal(false);
    } catch (error) {
      console.error('Error creating subcategory:', error);
      addToast({
        type: 'error',
        message: 'Failed to create subcategory',
      });
    }
  };

  const handleUpdateSubcategory = async (data: { name: string; categoryId: string }) => {
    if (!editingSubcategory) return;

    try {
      const { error } = await supabase
        .from('subcategories')
        .update({
          name: data.name,
          category_id: data.categoryId
        })
        .eq('id', editingSubcategory.id)
        .abortSignal(new AbortController().signal); // Disable cache for update

      if (error) throw error;

      addToast({
        type: 'success',
        message: 'Subcategory updated successfully',
      });
      setEditingSubcategory(null);
      setShowSubcategoryModal(false);
    } catch (error) {
      console.error('Error updating subcategory:', error);
      addToast({
        type: 'error',
        message: 'Failed to update subcategory',
      });
    }
  };

  const handleDeleteCategory = async (id: string) => {
    setCategoryToDelete(id);
    setIsDeleteCategoryModalOpen(true);
  };

  const handleConfirmDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', categoryToDelete);

      if (error) throw error;

      addToast({
        type: 'success',
        message: 'Category deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      addToast({
        type: 'error',
        message: 'Failed to delete category',
      });
    } finally {
      setIsDeleteCategoryModalOpen(false);
      setCategoryToDelete(null);
    }
  };

  const handleDeleteSubcategory = async (id: string) => {
    setSubcategoryToDelete(id);
    setIsDeleteSubcategoryModalOpen(true);
  };

  const handleConfirmDeleteSubcategory = async () => {
    if (!subcategoryToDelete) return;

    try {
      const { error } = await supabase
        .from('subcategories')
        .delete()
        .eq('id', subcategoryToDelete);

      if (error) throw error;

      addToast({
        type: 'success',
        message: 'Subcategory deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      addToast({
        type: 'error',
        message: 'Failed to delete subcategory',
      });
    } finally {
      setIsDeleteSubcategoryModalOpen(false);
      setSubcategoryToDelete(null);
    }
  };

  const handleCreateProvider = async (data: { name: string; icon: string }) => {
    try {
      const { data: newProvider, error } = await supabase
        .from('providers')
        .insert([{ name: data.name, icon: data.icon }])
        .select()
        .single();

      if (error) throw error;

      // Update local state immediately
      if (newProvider) {
        setProviders(prevProviders => [newProvider, ...prevProviders]);
      }

      addToast({
        type: 'success',
        message: 'Provider created successfully',
      });
      setShowProviderModal(false);
    } catch (error) {
      console.error('Error creating provider:', error);
      addToast({
        type: 'error',
        message: 'Failed to create provider',
      });
    }
  };

  const handleUpdateProvider = async (data: { name: string; icon: string }) => {
    if (!editingProvider) return;

    try {
      const { error } = await supabase
        .from('providers')
        .update({ name: data.name, icon: data.icon })
        .eq('id', editingProvider.id)
        .abortSignal(new AbortController().signal);

      if (error) throw error;

      // Update local state immediately
      setProviders(prevProviders =>
        prevProviders.map(p =>
          p.id === editingProvider.id
            ? { ...p, name: data.name, icon: data.icon }
            : p
        )
      );

      addToast({
        type: 'success',
        message: 'Provider updated successfully',
      });
      setEditingProvider(null);
      setShowProviderModal(false);
    } catch (error) {
      console.error('Error updating provider:', error);
      addToast({
        type: 'error',
        message: 'Failed to update provider',
      });
    }
  };

  const handleDeleteProvider = async (id: string) => {
    setProviderToDelete(id);
    setIsDeleteProviderModalOpen(true);
  };

  const handleConfirmDeleteProvider = async () => {
    if (!providerToDelete) return;

    try {
      const { error } = await supabase
        .from('providers')
        .delete()
        .eq('id', providerToDelete);

      if (error) throw error;

      // Update local state immediately
      setProviders(prevProviders => prevProviders.filter(p => p.id !== providerToDelete));

      addToast({
        type: 'success',
        message: 'Provider deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting provider:', error);
      addToast({
        type: 'error',
        message: 'Failed to delete provider',
      });
    } finally {
      setIsDeleteProviderModalOpen(false);
      setProviderToDelete(null);
    }
  };

  const sections = [
    {
      name: 'Menu Categories',
      content: (
        <div className="space-y-6 pt-4">
          {/* Categories Section */}
          <div className="bg-white dark:bg-dark-bg-light shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary">
                Categories
              </h2>
              <button
                onClick={() => {
                  setEditingCategory(null);
                  setShowCategoryModal(true);
                }}
                className="inline-flex items-center px-3 py-2 border border-transparent rounded-lg 
                  text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
                  transition-colors duration-200"
              >
                <i className="fas fa-plus mr-2" />
                Add Category
              </button>
            </div>

            {/* Categories List */}
            <div className="space-y-3">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-bg-lighter 
                    rounded-lg border border-gray-100 dark:border-dark-border
                    transition-shadow duration-200 hover:shadow-sm cursor-pointer"
                  onClick={() => {
                    setEditingCategory(category);
                    setShowCategoryModal(true);
                  }}
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg 
                      bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400">
                      <i className={`fas ${category.icon}`} />
                    </div>
                    <div>
                      <span className="font-medium text-gray-900 dark:text-dark-text-primary">
                        {category.name}
                      </span>
                      <span className="ml-2 text-sm text-gray-500 dark:text-dark-text-secondary">
                        ({subcategories.filter(s => s.category_id === category.id).length} subcategories)
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCategory(category.id);
                    }}
                    className="p-2 rounded-lg text-red-600 dark:text-red-400 
                      hover:bg-red-50 dark:hover:bg-red-900/20
                      transition-colors duration-200"
                  >
                    <i className="fas fa-trash" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Subcategories Section */}
          <div className="bg-white dark:bg-dark-bg-light shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary">
                Subcategories
              </h2>
              <button
                onClick={() => {
                  setEditingSubcategory(null);
                  setShowSubcategoryModal(true);
                }}
                className="inline-flex items-center px-3 py-2 border border-transparent rounded-lg 
                  text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
                  transition-colors duration-200"
              >
                <i className="fas fa-plus mr-2" />
                Add Subcategory
              </button>
            </div>

            {/* Subcategories List */}
            <div className="space-y-6">
              {categories.map((category) => {
                const categorySubcategories = subcategories.filter(
                  s => s.category_id === category.id
                );

                if (categorySubcategories.length === 0) return null;

                return (
                  <div key={category.id} className="space-y-3">
                    <div className="flex items-center space-x-2 text-sm font-medium text-gray-500 dark:text-dark-text-secondary">
                      <i className={`fas ${category.icon}`} />
                      <span>{category.name}</span>
                    </div>
                    <div className="space-y-2 ml-6">
                      {categorySubcategories.map((subcategory) => (
                        <div
                          key={subcategory.id}
                          className="flex items-center justify-between p-3 
                            bg-gray-50 dark:bg-dark-bg-lighter rounded-lg
                            border border-gray-100 dark:border-dark-border
                            transition-shadow duration-200 hover:shadow-sm cursor-pointer"
                          onClick={() => {
                            setEditingSubcategory(subcategory);
                            setShowSubcategoryModal(true);
                          }}
                        >
                          <span className="text-gray-900 dark:text-dark-text-primary">
                            {subcategory.name}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSubcategory(subcategory.id);
                            }}
                            className="p-2 rounded-lg text-red-600 dark:text-red-400 
                              hover:bg-red-50 dark:hover:bg-red-900/20
                              transition-colors duration-200"
                          >
                            <i className="fas fa-trash" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )
    },
    {
      name: 'Providers',
      content: (
        <div className="bg-white dark:bg-dark-bg-light shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary">
              Providers Management
            </h2>
            <button
              onClick={() => {
                setEditingProvider(null);
                setShowProviderModal(true);
              }}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-lg 
                text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
                transition-colors duration-200"
            >
              <i className="fas fa-plus mr-2" />
              Add Provider
            </button>
          </div>

          <div className="space-y-3">
            {providers.map((provider) => (
              <div
                key={provider.id}
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-bg-lighter 
                  rounded-lg border border-gray-100 dark:border-dark-border
                  transition-shadow duration-200 hover:shadow-sm cursor-pointer"
                onClick={() => {
                  setEditingProvider(provider);
                  setShowProviderModal(true);
                }}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg 
                    bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400">
                    <i className={`fas ${provider.icon}`} />
                  </div>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-dark-text-primary">
                      {provider.name}
                    </span>
                    <span className="ml-2 text-sm text-gray-500 dark:text-dark-text-secondary">
                      {new Date(provider.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProvider(provider.id);
                    }}
                    className="p-2 rounded-lg text-red-600 dark:text-red-400 
                      hover:bg-red-50 dark:hover:bg-red-900/20
                      transition-colors duration-200"
                  >
                    <i className="fas fa-trash" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen-75">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pt-4">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary mb-6">
        Settings
      </h1>

      <Tab.Group>
        <Tab.List className="flex space-x-1 rounded-xl bg-primary-900/10 dark:bg-primary-100/10 p-1 mb-6">
          {sections.map((section) => (
            <Tab
              key={section.name}
              className={({ selected }) =>
                classNames(
                  'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                  'ring-white/60 ring-offset-2 ring-offset-primary-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-white dark:bg-dark-bg-light text-primary-700 dark:text-primary-400 shadow'
                    : 'text-gray-600 dark:text-dark-text-secondary hover:bg-white/[0.12] hover:text-primary-600'
                )
              }
            >
              {section.name}
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          {sections.map((section) => (
            <Tab.Panel
              key={section.name}
              className={classNames(
                'rounded-xl bg-white dark:bg-dark-bg p-3',
                'ring-white/60 ring-offset-2 ring-offset-primary-400 focus:outline-none focus:ring-2'
              )}
            >
              {section.content}
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>

      {/* Modals */}
      {showCategoryModal && (
        <CategoryModal
          category={editingCategory || undefined}
          availableIcons={availableIcons}
          onClose={() => {
            setEditingCategory(null);
            setShowCategoryModal(false);
          }}
          onSubmit={editingCategory ? handleUpdateCategory : handleCreateCategory}
        />
      )}

      {showSubcategoryModal && (
        <SubcategoryModal
          subcategory={editingSubcategory || undefined}
          categories={categories}
          onClose={() => {
            setEditingSubcategory(null);
            setShowSubcategoryModal(false);
          }}
          onSubmit={editingSubcategory ? handleUpdateSubcategory : handleCreateSubcategory}
        />
      )}

      <ProviderModal
        isOpen={showProviderModal}
        onClose={() => {
          setEditingProvider(null);
          setShowProviderModal(false);
        }}
        onSubmit={editingProvider ? handleUpdateProvider : handleCreateProvider}
        provider={editingProvider}
        title={editingProvider ? 'Edit Provider' : 'Add Provider'}
        availableIcons={availableIcons}
      />

      {/* Category Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteCategoryModalOpen}
        onClose={() => {
          setIsDeleteCategoryModalOpen(false);
          setCategoryToDelete(null);
        }}
        title="Delete Category"
        actions={
          <>
            <button
              onClick={() => {
                setIsDeleteCategoryModalOpen(false);
                setCategoryToDelete(null);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:text-dark-text-primary dark:bg-dark-bg dark:hover:bg-dark-bg-light rounded-lg border border-gray-300 dark:border-dark-border"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDeleteCategory}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
            >
              Delete
            </button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-dark-text-secondary">
          Are you sure you want to delete this category? This will also delete all its subcategories. This action cannot be undone.
        </p>
      </Modal>

      {/* Subcategory Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteSubcategoryModalOpen}
        onClose={() => {
          setIsDeleteSubcategoryModalOpen(false);
          setSubcategoryToDelete(null);
        }}
        title="Delete Subcategory"
        actions={
          <>
            <button
              onClick={() => {
                setIsDeleteSubcategoryModalOpen(false);
                setSubcategoryToDelete(null);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:text-dark-text-primary dark:bg-dark-bg dark:hover:bg-dark-bg-light rounded-lg border border-gray-300 dark:border-dark-border"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDeleteSubcategory}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
            >
              Delete
            </button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-dark-text-secondary">
          Are you sure you want to delete this subcategory? This action cannot be undone.
        </p>
      </Modal>

      {/* Provider Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteProviderModalOpen}
        onClose={() => {
          setIsDeleteProviderModalOpen(false);
          setProviderToDelete(null);
        }}
        title="Delete Provider"
        actions={
          <>
            <button
              onClick={() => {
                setIsDeleteProviderModalOpen(false);
                setProviderToDelete(null);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:text-dark-text-primary dark:bg-dark-bg dark:hover:bg-dark-bg-light rounded-lg border border-gray-300 dark:border-dark-border"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDeleteProvider}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
            >
              Delete
            </button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-dark-text-secondary">
          Are you sure you want to delete this provider? This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
}